import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AddCommentIcon from '@mui/icons-material/AddComment';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonIcon from '@mui/icons-material/Person';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SendIcon from '@mui/icons-material/Send';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Fade,
  Grow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment-timezone';

import { makeAuthenticatedApiCall } from '../../apiHelper';
import { SKIPCAR_CG_PUBLIC_AUTHOR } from '../../constants/comments';
import { useExternalCommentsDA } from '../../hooks/useApiQueries';
import { useAuth } from '../AuthContext';
import FilesOpener from '../CGForm/FilesOpener';
import FirebaseUploadComponent from '../CreateCotation/FirebaseUploadComponent';
import FileCarouselDialog from '../FileCarouselDialog';
import CommentsDrawer from './CommentsDrawer';

interface ExternalComment {
  comment_id: number;
  registration_id: number;
  user_id: number;
  user_role: string;
  user_name: string;
  comment: string;
  created_at: string;
  files?: string; // JSON string des fichiers
}

interface ExternalCommentsDrawerProps {
  open: boolean;
  onClose: () => void;
  registrationId: number;
  userRole: string;
  type: 'cg' | 'da';
  onCommentAdded?: () => Promise<void>;
  regPlate?: string;
}

const ExternalCommentsDrawer: React.FC<ExternalCommentsDrawerProps> = ({
  open,
  onClose,
  registrationId,
  userRole,
  type,
  onCommentAdded,
  regPlate,
}) => {
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [comments, setComments] = useState<ExternalComment[]>([]);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');
  const theme = useTheme();
  const { currentUser } = useAuth();

  // Carousel state
  const [carouselOpen, setCarouselOpen] = useState<boolean>(false);
  const [carouselIndex, setCarouselIndex] = useState<number>(0);

  const parseCommentFiles = (files: string | string[] | object | undefined | null): string[] => {
    if (!files || files === 'NULL') return [];
    if (Array.isArray(files)) return files;
    if (typeof files === 'string') {
      try {
        const parsed = JSON.parse(files);
        if (Array.isArray(parsed)) return parsed;
        return files.split(',').map((u) => u.trim());
      } catch {
        return files.split(',').map((u) => u.trim());
      }
    }
    return [];
  };

  const allCarouselFiles = useMemo(() => {
    const urls: string[] = [];
    comments.forEach((comment) => {
      parseCommentFiles(comment.files).forEach((u) => {
        if (u && !urls.includes(u)) urls.push(u);
      });
    });
    return urls;
  }, [comments]);

  const handleCarouselOpen = (url: string) => {
    const index = allCarouselFiles.indexOf(url);
    setCarouselIndex(index >= 0 ? index : 0);
    setCarouselOpen(true);
  };

  // Pour les DA, on utilise le hook existant, pour les CG on fait un fetch manuel
  const { data: daComments, refetch: refetchDAComments } =
    type === 'da' ? useExternalCommentsDA(registrationId) : { data: undefined, refetch: () => Promise.resolve() };

  useEffect(() => {
    if (type === 'da' && daComments) {
      setComments(daComments);
    } else if (type === 'cg') {
      fetchCGComments();
    }
  }, [type, daComments, registrationId]);

  const fetchCGComments = async () => {
    try {
      const response = await makeAuthenticatedApiCall('get', `/api/cg/external-comments/${registrationId}`);
      setComments(response.data as ExternalComment[]);
    } catch (error) {
      console.error('Erreur lors du chargement des commentaires externes CG:', JSON.stringify(error, null, 2));
    }
  };

  // Fonctions pour gérer l'upload de fichiers
  const handleUploadComplete = (url: string) => {
    setUploadedUrls((prevUrls) => [...prevUrls, url]);
  };

  const handleDeleteComplete = (url: string) => {
    setUploadedUrls((prevUrls) => prevUrls.filter((prevUrl) => prevUrl !== url));
  };

  const handleAddComment = async (commentType: string) => {
    // Pour les commentaires normaux, permettre l'envoi si il y a du texte OU au moins un fichier
    if (commentType === 'sendComment' && !newComment.trim() && uploadedUrls.length === 0) return;
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const endpoint = type === 'cg' ? '/api/cg/external-comments' : '/api/da/external-comments';
      await makeAuthenticatedApiCall('post', endpoint, {
        registrationId,
        comment: commentType === 'paymentReminder' ? 'Relance de paiement' : newComment,
        type: commentType,
        files: uploadedUrls.length > 0 ? uploadedUrls : undefined, // Inclure les fichiers uploadés
      });

      setNewComment('');
      setUploadedUrls([]); // Réinitialiser les fichiers uploadés

      if (type === 'da') {
        await Promise.all([refetchDAComments(), queryClient.invalidateQueries({ queryKey: ['useDAList'] })]);
      } else {
        await fetchCGComments();
      }

      // Appeler le callback pour rafraîchir les données dans le parent
      if (onCommentAdded) {
        await onCommentAdded();
      }

      // Fermer le drawer après l'ajout réussi du commentaire
      onClose();
    } catch (error) {
      console.error("Erreur lors de l'ajout du commentaire externe:", JSON.stringify(error, null, 2));
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCommentDate = (dateString: string) => {
    return moment(dateString).tz('Europe/Paris').format('DD/MM/YYYY à HH:mm');
  };

  // Vue agence : tous les comptes Skipcar sont fondus dans un intitulé unique.
  // Vue interne (god/arbitrator/head) : on montre qui a écrit — demande
  // François du 27/08/2026, le masquage s'appliquait aussi entre nous.
  const isAgencyViewer = userRole === 'agency';

  const getInitials = (name: string, role: string) => {
    if (role !== 'agency' && isAgencyViewer) return 'CG';

    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (role: string) => {
    // Vue agence : un seul vert pour toute l'équipe Skipcar, aucune nuance qui
    // permettrait de deviner qui écrit. En interne on retombe sur roleColors.
    if (role !== 'agency' && isAgencyViewer) return '#4caf50';

    const roleColors: { [key: string]: string } = {
      agency: '#ff9800',
      arbitrator: '#9c27b0',
      god: '#f44336',
    };

    return roleColors[role] || '#2196f3';
  };

  const getUserDisplayName = (comment: ExternalComment) => {
    if (comment.user_role !== 'agency' && isAgencyViewer) return SKIPCAR_CG_PUBLIC_AUTHOR;

    return `${comment.user_name} (${t(`${comment.user_role}`)})`;
  };

  const isPaymentReminder = (comment: ExternalComment) => {
    return comment.comment === 'Relance de paiement';
  };

  const getCommentTypeChip = (comment: ExternalComment) => {
    // Si le rôle est god ou arbitrator, c'est un commentaire interne
    if (comment.user_role === 'god' || comment.user_role === 'arbitrator') {
      return {
        label: 'Skipcar',
        color: 'primary' as const,
      };
    }
    // Sinon (agency), c'est un commentaire externe
    return {
      label: 'Agence',
      color: 'warning' as const,
    };
  };

  return (
    <>
    <CommentsDrawer
      open={open}
      onClose={onClose}
      title={`Commentaires externes (${comments.length})${regPlate ? ` - ${regPlate}` : ''}`}
      width={520}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Formulaire d'ajout de commentaire */}
        <Fade in={open} timeout={600}>
          <Card
            elevation={2}
            sx={{
              background: 'linear-gradient(135deg, #fff3e0 0%, #f1f8e9 100%)',
              border: '1px solid #e0e0e0',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AddCommentIcon sx={{ mr: 1, color: theme.palette.warning.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Ajouter un commentaire externe
                </Typography>
              </Box>

              <TextField
                label="Votre commentaire"
                variant="outlined"
                multiline
                rows={3}
                fullWidth
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={
                  uploadedUrls.length > 0
                    ? 'Commentaire optionnel (fichiers déjà ajoutés)'
                    : 'Saisissez votre commentaire ici...'
                }
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': {
                    fontWeight: 'normal !important',
                  },
                  '& .MuiInputBase-input': {
                    fontWeight: 'normal !important',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    fontWeight: 'normal !important',
                  },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.warning.main,
                      },
                    },
                  },
                }}
              />

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  Fichiers joints (optionnel)
                </Typography>
                <FirebaseUploadComponent
                  onUploadComplete={handleUploadComplete}
                  onDeleteComplete={handleDeleteComplete}
                  uploadedUrls={uploadedUrls}
                  source={currentUser?.uid}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  onClick={() => handleAddComment('sendComment')}
                  disabled={(!newComment.trim() && uploadedUrls.length === 0) || isSubmitting}
                  startIcon={<SendIcon />}
                  variant="contained"
                  className="dealActionButton"
                  sx={{
                    px: '24px !important',
                    py: 1,
                    fontWeight: 600,
                    backgroundColor: theme.palette.warning.main,
                    '&:hover': {
                      backgroundColor: theme.palette.warning.dark,
                    },
                  }}
                >
                  {isSubmitting ? 'Envoi...' : 'Envoyer'}
                </Button>

                {userRole !== 'agency' && (
                  <Button
                    onClick={() => handleAddComment('paymentReminder')}
                    disabled={isSubmitting}
                    startIcon={<PaymentIcon />}
                    variant="contained"
                    className="secondaryDealActionButton"
                    sx={{
                      px: '24px !important',
                      py: 1,
                      fontWeight: 600,
                      backgroundColor: '#e91e63',
                      '&:hover': {
                        backgroundColor: '#ad1457',
                      },
                    }}
                  >
                    Relance paiement
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Fade>

        {/* Liste des commentaires */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {comments.length === 0 ? (
            <Fade in={open} timeout={800}>
              <Card elevation={1} sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body1" color="text.secondary">
                  Aucun commentaire externe pour le moment
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Les commentaires apparaîtront ici.
                </Typography>
              </Card>
            </Fade>
          ) : (
            comments.map((comment, index) => (
              <Grow key={comment.comment_id} in={open} timeout={600 + index * 100} style={{ transformOrigin: '0 0 0' }}>
                <Card
                  elevation={1}
                  sx={{
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      elevation: 3,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                    },
                    ...(isPaymentReminder(comment) && {
                      background: 'linear-gradient(135deg, #ffebee 0%, #fce4ec 100%)',
                      border: '1px solid #e91e6330',
                    }),
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Header du commentaire */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: getAvatarColor(comment.user_role),
                          width: 40,
                          height: 40,
                          mr: 2,
                          fontWeight: 600,
                        }}
                      >
                        {getInitials(comment.user_name, comment.user_role)}
                      </Avatar>

                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {getUserDisplayName(comment)}
                          </Typography>

                          <Chip
                            label={getCommentTypeChip(comment).label}
                            size="small"
                            color={getCommentTypeChip(comment).color}
                            variant="outlined"
                            sx={{ ml: 1, fontSize: '0.7rem', fontWeight: 600 }}
                          />

                          {isPaymentReminder(comment) && (
                            <Chip
                              label="Relance"
                              size="small"
                              color="error"
                              variant="filled"
                              icon={<PaymentIcon sx={{ fontSize: 14 }} />}
                              sx={{ ml: 0.5, fontSize: '0.7rem', fontWeight: 600 }}
                            />
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {formatCommentDate(comment.created_at)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Contenu du commentaire */}
                    <Typography
                      variant="body2"
                      sx={{
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        ...(isPaymentReminder(comment) && {
                          fontWeight: 600,
                          color: '#e91e63',
                        }),
                      }}
                    >
                      {comment.comment}
                    </Typography>

                    {/* Fichiers joints */}
                    {comment.files && comment.files !== 'NULL' && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                          Fichiers joints :
                        </Typography>
                        <FilesOpener
                          onFileClick={handleCarouselOpen}
                          FilesUrls={(() => {
                            // Vérifier que comment.files existe
                            if (!comment.files) {
                              return '';
                            }

                            // Si c'est déjà un tableau (nouveau format MySQL JSON)
                            if (Array.isArray(comment.files)) {
                              return comment.files.join(',');
                            }

                            // Si c'est une chaîne, essayer de la parser
                            if (typeof comment.files === 'string') {
                              try {
                                // Essayer de parser comme JSON (nouveau format)
                                const parsed = JSON.parse(comment.files);
                                if (Array.isArray(parsed)) {
                                  return parsed.join(',');
                                }
                                // Si ce n'est pas un tableau, retourner tel quel
                                return comment.files;
                              } catch (error) {
                                // Si le parsing échoue, c'est probablement une URL directe (ancien format)
                                return comment.files;
                              }
                            }

                            // Si c'est un objet mais pas un tableau, essayer de le convertir
                            if (typeof comment.files === 'object') {
                              try {
                                return JSON.stringify(comment.files);
                              } catch (error) {
                                return '';
                              }
                            }

                            return '';
                          })()}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grow>
            ))
          )}
        </Box>
      </Box>
    </CommentsDrawer>

    <FileCarouselDialog
      allFiles={allCarouselFiles}
      initialIndex={carouselIndex}
      open={carouselOpen}
      onClose={() => setCarouselOpen(false)}
    />
    </>
  );
};

export default ExternalCommentsDrawer;
